import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {
  useSession,
  signUp,
  signIn,
  signOut,
  authClient,
} from "@/lib/auth-client";

interface InvitationInfo {
  id: string;
  email: string;
  organizationName: string;
  organizationSlug: string;
  role: string;
  status: string;
}

interface AcceptInvitationParams {
  invitationId: string;
}

interface AcceptInvitationResult {
  error?: { message?: string };
}

interface InvitationPreview {
  email: string;
  organizationName: string;
  expiresAt: string;
}

export function InvitePage() {
  const { token: invitationId } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data: session, isPending: sessionLoading } = useSession();

  const [inviteInfo, setInviteInfo] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);

  // Public preview data (fetched without auth for email pre-fill)
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Tab state for unauthenticated users
  const [authTab, setAuthTab] = useState(0); // 0 = magic link, 1 = password

  // Magic link form state
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);

  // Sign up form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [signingUp, setSigningUp] = useState(false);

  // Fetch invitation details (only when authenticated)
  const fetchInvitation = useCallback(async () => {
    if (!invitationId) {
      setError("Invalid invitation link");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/organization/get-invitation?id=${invitationId}`,
        { credentials: "include" },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setErrorCode(data.code || null);
        throw new Error(data.message || "Invalid or expired invitation");
      }

      const data = await response.json();
      setInviteInfo({
        id: data.id,
        email: data.email,
        organizationName:
          data.organizationName || data.organization?.name || "Organization",
        organizationSlug:
          data.organizationSlug || data.organization?.slug || "",
        role: data.role || "member",
        status: data.status || "pending",
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid invitation");
    } finally {
      setLoading(false);
    }
  }, [invitationId]);

  // Fetch invitation when session becomes available
  useEffect(() => {
    if (session && invitationId && !inviteInfo) {
      fetchInvitation();
    }
  }, [session, invitationId, inviteInfo, fetchInvitation]);

  // Fetch public preview on mount (for email pre-fill - no auth required)
  useEffect(() => {
    if (!invitationId) {
      setPreviewLoading(false);
      return;
    }

    // Only fetch preview if not authenticated (authenticated users get full details)
    if (session) {
      setPreviewLoading(false);
      return;
    }

    const fetchPreview = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/invitations/${invitationId}/preview`,
        );

        if (response.status === 410) {
          // Invitation expired
          const data = await response.json().catch(() => ({}));
          setPreviewError("This invitation has expired");
          if (data.organizationName) {
            setPreview({
              email: "",
              organizationName: data.organizationName,
              expiresAt: "",
            });
          }
          return;
        }

        if (!response.ok) {
          setPreviewError("Invalid invitation");
          return;
        }

        const data: InvitationPreview = await response.json();
        setPreview(data);

        // Pre-fill email fields
        setMagicLinkEmail(data.email);
        setEmail(data.email);
      } catch {
        setPreviewError("Failed to load invitation");
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [invitationId, session]);

  // Accept invitation
  const handleAcceptInvitation = async () => {
    if (!invitationId) return;

    setAccepting(true);
    setError(null);

    try {
      const acceptInvitation = (
        authClient as unknown as {
          organization: {
            acceptInvitation: (
              params: AcceptInvitationParams,
            ) => Promise<AcceptInvitationResult>;
          };
        }
      ).organization.acceptInvitation;

      const result = await acceptInvitation({ invitationId });

      if (result.error) {
        setError(result.error.message || "Failed to accept invitation");
        setAccepting(false);
        return;
      }

      // Success - redirect to dashboard
      navigate("/");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to accept invitation",
      );
      setAccepting(false);
    }
  };

  // Sign out (for wrong account scenario)
  const handleSignOut = async () => {
    await signOut();
    // After sign out, page will re-render and show unauthenticated flow
  };

  // Send magic link
  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicLinkError(null);
    setSendingMagicLink(true);

    try {
      // Send magic link with callback to this invite page
      const callbackURL = `${window.location.origin}/invite/${invitationId}`;

      const result = await signIn.magicLink({
        email: magicLinkEmail,
        callbackURL,
      });

      if (result.error) {
        setMagicLinkError(result.error.message || "Failed to send magic link");
        setSendingMagicLink(false);
        return;
      }

      setMagicLinkSent(true);
    } catch (err) {
      setMagicLinkError(
        err instanceof Error ? err.message : "Failed to send magic link",
      );
    } finally {
      setSendingMagicLink(false);
    }
  };

  // Sign up with password
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignUpError(null);

    if (password !== confirmPassword) {
      setSignUpError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setSignUpError("Password must be at least 8 characters");
      return;
    }

    setSigningUp(true);

    try {
      const signUpResult = await signUp.email({
        email,
        password,
        name,
      });

      if (signUpResult.error) {
        setSignUpError(
          signUpResult.error.message || "Failed to create account",
        );
        setSigningUp(false);
        return;
      }

      // After sign up, the session will be established
      // The useEffect will fetch invitation and we'll show accept button
    } catch (err) {
      setSignUpError(
        err instanceof Error ? err.message : "Failed to create account",
      );
      setSigningUp(false);
    }
  };

  // Loading session state
  if (sessionLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  // No invitation ID
  if (!invitationId) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Card sx={{ maxWidth: 400, p: { xs: 3, md: 4 }, textAlign: "center" }}>
          <Typography
            variant="h6"
            fontWeight={700}
            color="error"
            sx={{ mb: 1 }}
          >
            Invalid Invitation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This invitation link is invalid.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </Card>
      </Box>
    );
  }

  // Authenticated user - show invitation details and accept button
  if (session) {
    // Loading invitation details
    if (loading) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.50",
          }}
        >
          <CircularProgress size={48} />
        </Box>
      );
    }

    // Wrong account error - show helpful message with sign out option
    if (errorCode === "YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION") {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.50",
          }}
        >
          <Card
            sx={{ maxWidth: 400, p: { xs: 3, md: 4 }, textAlign: "center" }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              color="warning.main"
              sx={{ mb: 1 }}
            >
              Wrong Account
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              This invitation was sent to a different email address.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You&apos;re currently signed in as{" "}
              <strong>{session?.user.email}</strong>. Please sign out and access
              this link with the invited email.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
              <Button variant="contained" onClick={handleSignOut}>
                Sign Out
              </Button>
              <Button variant="outlined" onClick={() => navigate("/")}>
                Go to Dashboard
              </Button>
            </Box>
          </Card>
        </Box>
      );
    }

    // Other error fetching invitation
    if (error && !inviteInfo) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.50",
          }}
        >
          <Card
            sx={{ maxWidth: 400, p: { xs: 3, md: 4 }, textAlign: "center" }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              color="error"
              sx={{ mb: 1 }}
            >
              Invitation Error
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => navigate("/")}>
              Go to Dashboard
            </Button>
          </Card>
        </Box>
      );
    }

    const isCorrectEmail = session.user.email === inviteInfo?.email;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Card sx={{ maxWidth: 400, p: { xs: 3, md: 4 }, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <img
              src="/logos/agent-edge.png"
              alt="Agent Edge"
              style={{ height: 48, width: "auto" }}
            />
          </Box>
          <Typography
            variant="h5"
            fontWeight={700}
            color="text.primary"
            sx={{ mb: 1 }}
          >
            You&apos;re Invited!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            You&apos;ve been invited to join
          </Typography>
          <Typography
            variant="h6"
            color="primary"
            fontWeight={600}
            sx={{ mb: 2 }}
          >
            {inviteInfo?.organizationName}
          </Typography>

          {!isCorrectEmail && (
            <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
              This invitation was sent to <strong>{inviteInfo?.email}</strong>,
              but you&apos;re logged in as <strong>{session.user.email}</strong>
              .
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleAcceptInvitation}
            disabled={accepting}
          >
            {accepting ? "Accepting..." : "Accept Invitation"}
          </Button>

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 2, display: "block" }}
          >
            You&apos;ll be added as {inviteInfo?.role || "a member"}.
          </Typography>
        </Card>
      </Box>
    );
  }

  // Unauthenticated user - show sign in options
  // Show loading while fetching preview
  if (previewLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Show error if invitation is invalid or expired
  if (previewError && !preview) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Card sx={{ maxWidth: 400, p: { xs: 3, md: 4 }, textAlign: "center" }}>
          <Typography
            variant="h6"
            fontWeight={700}
            color="error"
            sx={{ mb: 1 }}
          >
            Invalid Invitation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {previewError}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </Card>
      </Box>
    );
  }

  // Show expired invitation message
  if (previewError === "This invitation has expired") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Card sx={{ maxWidth: 400, p: { xs: 3, md: 4 }, textAlign: "center" }}>
          <Typography
            variant="h6"
            fontWeight={700}
            color="warning.main"
            sx={{ mb: 1 }}
          >
            Invitation Expired
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This invitation to join{" "}
            <strong>{preview?.organizationName || "the organization"}</strong>{" "}
            has expired.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please contact your administrator to send a new invitation.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.50",
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: "100%", p: { xs: 3, md: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <img
            src="/logos/agent-edge.png"
            alt="Agent Edge"
            style={{ height: 48, width: "auto" }}
          />
        </Box>
        <Typography
          variant="h5"
          fontWeight={700}
          textAlign="center"
          sx={{ mb: 1 }}
        >
          You&apos;re Invited!
        </Typography>
        {preview ? (
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 3 }}
          >
            Sign in as <strong>{preview.email}</strong> to join{" "}
            <Typography component="span" color="primary" fontWeight={600}>
              {preview.organizationName}
            </Typography>
          </Typography>
        ) : (
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 3 }}
          >
            Sign in or create an account to accept your invitation
          </Typography>
        )}

        <Tabs
          value={authTab}
          onChange={(_, v) => setAuthTab(v)}
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab label="Magic Link" />
          <Tab label="Password" />
        </Tabs>

        {/* Magic Link Tab */}
        {authTab === 0 && (
          <>
            {magicLinkSent ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  Check your email!
                </Typography>
                <Typography variant="body2">
                  We sent a magic link to <strong>{magicLinkEmail}</strong>.
                  Click the link to sign in.
                </Typography>
              </Alert>
            ) : (
              <form onSubmit={handleSendMagicLink}>
                {magicLinkError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {magicLinkError}
                  </Alert>
                )}

                <TextField
                  label="Email Address"
                  type="email"
                  fullWidth
                  value={magicLinkEmail}
                  onChange={(e) => setMagicLinkEmail(e.target.value)}
                  placeholder="you@example.com"
                  sx={{ mb: 2 }}
                  required
                  autoComplete="email"
                  autoFocus={!preview}
                  slotProps={{
                    input: {
                      readOnly: !!preview,
                    },
                  }}
                  helperText={
                    preview
                      ? "Email pre-filled from your invitation"
                      : undefined
                  }
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={sendingMagicLink}
                >
                  {sendingMagicLink ? "Sending..." : "Send Magic Link"}
                </Button>

                {!preview && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mt: 2 }}
                  >
                    We&apos;ll email you a magic link for password-free sign in
                  </Typography>
                )}
              </form>
            )}
          </>
        )}

        {/* Password Sign Up Tab */}
        {authTab === 1 && (
          <form onSubmit={handleSignUp}>
            {signUpError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {signUpError}
              </Alert>
            )}

            <TextField
              label="Full Name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
              required
              autoComplete="name"
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              required
              autoComplete="email"
              slotProps={{
                input: {
                  readOnly: !!preview,
                },
              }}
              helperText={
                preview ? "Email pre-filled from your invitation" : undefined
              }
            />

            <TextField
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              required
              autoComplete="new-password"
              helperText="At least 8 characters"
            />

            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
              required
              autoComplete="new-password"
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={signingUp}
            >
              {signingUp ? "Creating Account..." : "Create Account"}
            </Button>
          </form>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" textAlign="center">
          Already have an account?{" "}
          <Button
            variant="text"
            size="small"
            onClick={() => navigate(`/login?redirect=/invite/${invitationId}`)}
          >
            Sign In
          </Button>
        </Typography>
      </Card>
    </Box>
  );
}
